import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, NavLink, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu, X, LogOut, Languages, Monitor, Sun, Moon, PanelLeftClose, PanelLeftOpen,
  Inbox, PauseCircle, BarChart2, Users, CheckSquare,
  MessageSquare, Building2, GitBranch, FileText, Settings, BookOpen, Plug, Send, QrCode, CreditCard,
  Lightbulb,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAndroidBack } from '../hooks/useAndroidBack';
import { useUnread } from '../context/UnreadContext';
import { useTheme, type Theme } from '../context/ThemeContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { ProjectLockup } from '../components/ProjectLockup';
import { ProductIcon } from '../components/ProductIcon';
import { SnackbarProvider } from '../components/Snackbar';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import { CAPABILITIES } from '../lib/capabilities';
import { APP_SHELL_ROUTES, FULL_BLEED_ROUTES, INBOX_PATH } from '../lib/routeConfig';
import { useAppViewportHeight } from '../hooks/useAppViewportHeight';
import { useFitBelow } from '../hooks/useFitBelow';
import { usePushRegistration } from '../push/usePushRegistration';

// ── Nav item config ─────────────────────────────────────────────────────────
// `section` starts a new labeled group. `unread` marks the item that carries an
// unread badge (gated behind CAPABILITIES.unreadCounts until the backend lands).

type NavItemDef = {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  labelKey: string;
  minRole?: 'supervisor' | 'admin';
  section?: string;
  unread?: boolean;
};

const NAV_ITEMS: NavItemDef[] = [
  { to: INBOX_PATH,     end: true, icon: Inbox,         labelKey: 'nav.inbox',        section: 'nav.section_workspace', unread: true },
  { to: '/paused',                 icon: PauseCircle,   labelKey: 'nav.paused'       },
  { to: '/analytics',              icon: BarChart2,     labelKey: 'nav.analytics'    },
  { to: '/team',                   icon: Users,         labelKey: 'nav.team',        minRole: 'admin', section: 'nav.section_management' },
  { to: '/resolutions',            icon: CheckSquare,   labelKey: 'nav.resolutions', minRole: 'supervisor' },
  { to: '/canned',                 icon: MessageSquare, labelKey: 'nav.canned',      minRole: 'admin' },
  { to: '/knowledge',              icon: BookOpen,      labelKey: 'nav.knowledge',   minRole: 'admin' },
  { to: '/departments',            icon: Building2,     labelKey: 'nav.departments', minRole: 'admin' },
  { to: '/branches',               icon: GitBranch,     labelKey: 'nav.branches',    minRole: 'admin' },
  { to: '/templates',              icon: FileText,      labelKey: 'nav.templates',   minRole: 'admin' },
  { to: '/campaigns',              icon: Send,          labelKey: 'nav.campaigns',   minRole: 'admin' },
  { to: '/links',                  icon: QrCode,        labelKey: 'nav.links',       minRole: 'admin' },
  { to: '/integrations',           icon: Plug,          labelKey: 'nav.integrations', minRole: 'admin', section: 'nav.section_account' },
  { to: '/settings',               icon: Settings,      labelKey: 'nav.settings',    minRole: 'admin' },
  { to: '/billing',                icon: CreditCard,    labelKey: 'nav.billing',     minRole: 'admin' },
  // NO minRole, on purpose: the suggestion box is open to every agent of every
  // tenant, whatever their role. Adding a minRole here would break that.
  { to: '/suggestions',            icon: Lightbulb,     labelKey: 'nav.suggestions',  section: 'nav.section_help' },
];

function roleCanSee(role: string | undefined, minRole: 'supervisor' | 'admin' | undefined) {
  if (!minRole) return true;
  if (minRole === 'supervisor') return role === 'admin' || role === 'supervisor';
  return role === 'admin';
}

function initials(name?: string): string {
  if (!name) return '·';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '·';
}

// ── Unread badge (gated behind capability flag) ──────────────────────────────
// Backend Pack Phase 4 — count comes from the Inbox list poll (see
// UnreadContext), not a dedicated fetch here.
function UnreadBadge({ count }: { count?: number }) {
  const { t } = useTranslation();
  if (!CAPABILITIES.unreadCounts || !count) return null;
  return (
    <span
      className="absolute -top-1 min-w-[16px] h-4 px-1 rounded-corner-full
        bg-primary text-on-primary text-label-small flex items-center justify-center tabular-nums"
      style={{ insetInlineEnd: '-0.25rem' }}
      aria-label={t('nav.unread_badge', { count })}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// ── Navigation rail (≥960px) — compact icon + label, active pill ─────────────
function NavRailItems({ role, unreadTotal }: { role?: string; unreadTotal: number }) {
  const { t } = useTranslation();
  const items = NAV_ITEMS.filter((item) => roleCanSee(role, item.minRole));

  return (
    <ul className="m-0 flex list-none flex-col items-stretch gap-1 p-0">
      {items.map(({ to, end, icon: Icon, labelKey, section, unread }) => (
        <li key={to} className={section ? 'mt-2 first:mt-0' : undefined}>
          <NavLink
            to={to}
            end={end}
            title={t(labelKey)}
            className="group mx-auto flex size-11 items-center justify-center rounded-xl
              transition-colors duration-m3-short ease-m3-standard
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
          >
            {({ isActive }) => (
              <span
                className={[
                  'relative flex size-10 items-center justify-center rounded-xl',
                  isActive
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant group-hover:bg-on-surface/[0.07] group-hover:text-on-surface',
                ].join(' ')}
              >
                <Icon size={19} strokeWidth={isActive ? 2.15 : 1.8} aria-hidden="true" />
                <UnreadBadge count={unread ? unreadTotal : undefined} />
                <span className="sr-only">{t(labelKey)}</span>
              </span>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// ── Expanded drawer (280px) — grouped, full labels ───────────────────────────
function NavDrawerItems({
  role, onNavigate, unreadTotal,
}: {
  role?: string; onNavigate: () => void; unreadTotal: number;
}) {
  const { t } = useTranslation();
  const items = NAV_ITEMS.filter((item) => roleCanSee(role, item.minRole));

  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map(({ to, end, icon: Icon, labelKey, section, unread }, i) => (
        <li key={to} className={section && i !== 0 ? 'mt-2' : undefined}>
          {section && (
            <p className={`px-3 pb-1.5 text-[11px] font-semibold leading-5 text-on-surface-variant/75
              ${i !== 0 ? 'pt-3' : 'pt-1'}`}>
              {t(section)}
            </p>
          )}
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'group relative flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium',
                'transition-colors duration-m3-short ease-m3-standard',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
                isActive
                  ? 'bg-primary/[0.11] text-primary'
                  : 'text-on-surface-variant hover:bg-on-surface/[0.07] hover:text-on-surface',
              ].join(' ')
            }
          >
            {({ isActive }) => <>
            <span className={`relative flex size-8 shrink-0 items-center justify-center rounded-lg
              ${isActive ? 'bg-primary/[0.10]' : 'bg-transparent group-hover:bg-on-surface/[0.05]'}`}>
              <Icon size={18} strokeWidth={isActive ? 2.15 : 1.8} aria-hidden="true" />
              <UnreadBadge count={unread ? unreadTotal : undefined} />
            </span>
            <span className="truncate">{t(labelKey)}</span>
            </>}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

// ── User menu (avatar → Profile / Language / Theme / Logout) ──────────────────
const THEME_OPTS: { value: Theme; icon: LucideIcon; labelKey: string }[] = [
  { value: 'auto',  icon: Monitor, labelKey: 'usermenu.theme_system' },
  { value: 'light', icon: Sun,     labelKey: 'usermenu.theme_light' },
  { value: 'dark',  icon: Moon,    labelKey: 'usermenu.theme_dark' },
];

function UserMenu({ placement, expanded = false }: { placement: 'rail' | 'bar'; expanded?: boolean }) {
  const { t, i18n } = useTranslation();
  const { agent, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [floatingStyle, setFloatingStyle] = useState<CSSProperties>();
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';
  const portalCollapsedRail = placement === 'rail' && !expanded;

  // Focus trap + Esc + focus-return (§11); outside-click handled separately.
  useFocusTrap(open, menuRef, () => setOpen(false));
  // The top-bar menu opens downwards: on a landscape phone it scrolls inside itself instead of running past the screen.
  useFitBelow(menuRef, open && placement !== 'rail');

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        wrapRef.current
        && !wrapRef.current.contains(target)
        && !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // The compact desktop rail intentionally clips its own contents. Render its
  // account menu at the viewport level and anchor it beside the avatar so the
  // menu stays fully visible without widening or disturbing the rail.
  useLayoutEffect(() => {
    if (!open || !portalCollapsedRail) {
      setFloatingStyle(undefined);
      return;
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const gap = 12;
      const viewportPadding = 12;
      const menuWidth = 256;
      const rtl = currentLang === 'ar' || document.documentElement.dir === 'rtl';
      const preferredLeft = rtl
        ? rect.left - gap - menuWidth
        : rect.right + gap;
      const left = Math.min(
        Math.max(preferredLeft, viewportPadding),
        Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding),
      );

      setFloatingStyle({
        left,
        bottom: Math.max(viewportPadding, window.innerHeight - rect.bottom),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [currentLang, open, portalCollapsedRail]);

  async function handleLogout() {
    setOpen(false);
    // On success logout() reloads into /login itself. If the server could not confirm it (offline), stay signed in.
    if (!(await logout())) window.alert(t('auth.logout_failed'));
  }

  const panelPos =
    placement === 'rail' && expanded
      ? 'bottom-full inset-x-0 mb-2'
      : placement === 'rail'
        ? 'bottom-0 start-full ms-3'
      : 'top-full end-0 mt-2';
  const panelWidth = placement === 'rail' && expanded ? 'w-full' : 'w-64';

  const menuPanel = open ? (
    <div
      ref={menuRef}
      role="menu"
      data-testid="user-menu-panel"
      style={portalCollapsedRail ? floatingStyle : undefined}
      className={`m3-menu-enter ${portalCollapsedRail ? 'fixed' : `absolute ${panelPos}`}
        ${panelWidth} z-[70] rounded-corner-md bg-surface
        shadow-elevation-2 border border-outline-variant/40 p-2 ${placement === 'rail' ? '' : 'overflow-y-auto overscroll-contain'}`}
    >
      {/* Identity */}
      <div className="flex items-center gap-3 px-2 py-2">
        <span className="flex items-center justify-center w-10 h-10 rounded-corner-full
          bg-primary-container text-on-primary-container text-title-small font-medium shrink-0">
          {initials(agent?.name)}
        </span>
        <div className="min-w-0">
          <p className="text-title-small text-on-surface truncate">{agent?.name}</p>
          <p className="text-body-small text-on-surface-variant truncate" dir="ltr">{agent?.email}</p>
        </div>
      </div>

      <div className="my-1 border-t border-outline-variant/50" />

      {/* Language */}
      <div className="px-2 py-1.5">
        <p className="flex items-center gap-2 text-label-small uppercase tracking-wider text-on-surface-variant mb-1.5">
          <Languages size={13} aria-hidden="true" /> {t('usermenu.language')}
        </p>
        <div role="group" className="flex gap-1">
          {(['en', 'ar'] as const).map((lng) => {
            const active = currentLang === lng;
            return (
              <button
                key={lng}
                role="menuitemradio"
                data-testid={`lang-${lng}`}
                aria-checked={active}
                onClick={() => i18n.changeLanguage(lng)}
                className={[
                  'touch-target flex-1 h-8 rounded-corner-full text-label-large transition-colors duration-m3-short',
                  active
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'text-on-surface-variant hover:bg-on-surface/[0.08]',
                ].join(' ')}
              >
                {t(lng === 'en' ? 'usermenu.lang_en' : 'usermenu.lang_ar')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme */}
      <div className="px-2 py-1.5">
        <p className="text-label-small uppercase tracking-wider text-on-surface-variant mb-1.5">
          {t('usermenu.theme')}
        </p>
        <div role="group" className="flex gap-1">
          {THEME_OPTS.map(({ value, icon: Icon, labelKey }) => {
            const active = theme === value;
            return (
              <button
                key={value}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => setTheme(value)}
                title={t(labelKey)}
                className={[
                  'touch-target flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-corner-md',
                  'text-label-small transition-colors duration-m3-short',
                  active
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'text-on-surface-variant hover:bg-on-surface/[0.08]',
                ].join(' ')}
              >
                <Icon size={16} aria-hidden="true" />
                {t(labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="my-1 border-t border-outline-variant/50" />

      {/* Logout */}
      <button
        role="menuitem"
        onClick={() => void handleLogout()}
        className="touch-target w-full flex items-center gap-3 h-10 px-3 rounded-corner-md
          text-label-large text-on-surface hover:bg-on-surface/[0.08]
          transition-colors duration-m3-short"
      >
        <LogOut size={18} aria-hidden="true" />
        {t('nav.logout')}
      </button>
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('usermenu.open')}
        className={[
          'flex items-center transition-colors duration-m3-short ease-m3-standard',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
          expanded
            ? 'w-full gap-3 rounded-xl border border-outline-variant/35 bg-surface-container-low px-2.5 py-2 text-start hover:bg-surface-container-high'
            : `${placement === 'bar' ? 'size-11' : 'size-10'} justify-center rounded-corner-full bg-primary-container text-on-primary-container hover:bg-secondary-container`,
        ].join(' ')}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-corner-full
          bg-primary-container text-title-small font-medium text-on-primary-container">
          {initials(agent?.name)}
        </span>
        {expanded && (
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-on-surface">{agent?.name}</span>
            <span className="mt-0.5 block truncate text-xs text-on-surface-variant" dir="ltr">{agent?.email}</span>
          </span>
        )}
      </button>

      {portalCollapsedRail && menuPanel
        ? createPortal(menuPanel, document.body)
        : menuPanel}
    </div>
  );
}

// ── Layout ───────────────────────────────────────────────────────────────────
const RAIL_EXPANDED_KEY = 'wa.sidebarExpanded';

export function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Persistent desktop rail: compact (icon rail, w-20) vs pinned-open (full
  // labels, w-[280px]) — remembered per-browser so "keep it open" sticks
  // across sessions instead of resetting to compact every load.
  const [railExpanded, setRailExpanded] = useState(() => {
    try { return localStorage.getItem(RAIL_EXPANDED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(RAIL_EXPANDED_KEY, railExpanded ? '1' : '0'); } catch { /* storage unavailable */ }
  }, [railExpanded]);
  const { agent } = useAuth();
  const { total: unreadTotal } = useUnread();
  const { t } = useTranslation();
  const location = useLocation();
  const role = agent?.role;

  // APK: Android hardware back drives in-app navigation (minimize on the
  // top-level Inbox). No-op on web.
  useAndroidBack();

  // Native-only: register this device for push once authenticated, and deep-link
  // on notification taps. No-op in a browser (guarded by isNativePlatform()).
  usePushRegistration(!!agent);
  const drawerRef = useRef<HTMLElement>(null);

  // Modal drawer: focus trap + Esc + focus-return (§11).
  useFocusTrap(drawerOpen, drawerRef, () => setDrawerOpen(false));

  // Full-bleed routes (inbox master-detail + the read-only conversation
  // transcript) are declared as route patterns in one config module and matched
  // with the router's own matchPath — the shell holds no hand-rolled path logic.
  // (A route `handle` would need a data-router migration; matchPath over shared
  // metadata is the classic-<Routes>-compatible equivalent.)
  const fullBleed = FULL_BLEED_ROUTES.some((p) => matchPath(p, location.pathname) !== null);
  // The Inbox workspace is an app shell: exactly the visible viewport, its panes scrolling inside.
  const appShell = APP_SHELL_ROUTES.some((p) => matchPath(p, location.pathname) !== null);
  useAppViewportHeight(appShell);

  // Close the drawer on any route change. (Esc + focus-trap: useFocusTrap above.)
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <SnackbarProvider>
    <div className="min-h-screen bg-canvas">
      {/* ── Navigation rail (fixed, ≥960px) ──────────────────────────────── */}
      <aside
        data-testid="desktop-navigation"
        className={`fixed inset-y-3 start-3 z-40 hidden flex-col overflow-hidden rounded-[1.35rem]
          border border-outline-variant/45 bg-pane
          [box-shadow:0_1px_2px_rgb(15_23_42/0.04),0_12px_34px_rgb(15_23_42/0.055)]
          transition-[width] duration-m3-medium ease-m3-standard bp-md:flex
          ${railExpanded ? 'w-[252px]' : 'w-[68px]'}`}
        aria-label={t('nav.section_workspace')}
      >
        {/* Menu / logo header — compact icon stack, or a full lockup + collapse
            button once pinned open */}
        {railExpanded ? (
          <div className="flex h-[76px] shrink-0 items-center justify-between gap-2 px-4">
            <NavLink to={INBOX_PATH} end aria-label="Chatbot" className="min-w-0 rounded-corner-sm
              focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2">
              <ProjectLockup />
            </NavLink>
            <button
              onClick={() => setRailExpanded(false)}
              aria-label={t('nav.collapse_sidebar')}
              className="flex size-9 shrink-0 items-center justify-center rounded-xl text-on-surface-variant
                transition-colors duration-m3-short hover:bg-on-surface/[0.07] hover:text-on-surface
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
            >
              <PanelLeftClose size={19} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 flex-col items-center gap-2 py-3">
            <NavLink to={INBOX_PATH} end aria-label="Chatbot" className="flex size-10 items-center justify-center rounded-xl
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35">
              <ProductIcon size={23} />
            </NavLink>
            <button
              onClick={() => setRailExpanded(true)}
              aria-label={t('nav.expand_sidebar')}
              className="flex size-9 items-center justify-center rounded-xl text-on-surface-variant
                transition-colors duration-m3-short hover:bg-on-surface/[0.07] hover:text-on-surface
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
            >
              <PanelLeftOpen size={18} aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Scrollable nav */}
        <nav className={`min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3
          ${railExpanded ? 'px-3' : 'px-1.5'}`}>
          {railExpanded
            ? <NavDrawerItems role={role} onNavigate={() => {}} unreadTotal={unreadTotal} />
            : <NavRailItems role={role} unreadTotal={unreadTotal} />}
        </nav>

        {/* User menu at the rail bottom */}
        <div className={`shrink-0 p-3 ${railExpanded ? '' : 'flex justify-center'}`}
        >
          <UserMenu placement="rail" expanded={railExpanded} />
        </div>
      </aside>

      {/* ── Content column (offset by the rail on desktop) ───────────────── */}
      <div className={`flex flex-col transition-[margin-inline-start]
        duration-m3-medium ease-m3-standard ${railExpanded ? 'bp-md:ms-[267px]' : 'bp-md:ms-[83px]'}
        ${appShell ? 'app-viewport-h' : 'min-h-screen'}`}>
        {/* Mobile top app bar (<960px) */}
        <header className="hide-when-short sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 border-b
          border-outline-variant/50 bg-pane px-2.5 bp-md:hidden [@media(max-height:500px)]:h-14">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label={t('nav.open_menu')}
            className="flex size-11 items-center justify-center rounded-xl text-on-surface-variant
              transition-colors duration-m3-short hover:bg-on-surface/[0.07] hover:text-on-surface
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
          >
            <Menu size={22} aria-hidden="true" />
          </button>
          <ProjectLockup />
          <div className="ms-auto">
            <UserMenu placement="bar" />
          </div>
        </header>

        {/* Subscription reminder — always-visible banner above all page content
            when renewal is ≤3 days away or overdue (fetches my-subscription once). */}
        <SubscriptionBanner />

        {/* Page content — data-dense workspace routes go full-bleed and manage
            their own content width; every other page stays in a comfortable
            centered column. */}
        <main className={`flex-grow w-full min-h-0 ${appShell ? 'flex flex-col' : ''}`}>
          {appShell ? (
            <div data-testid="full-bleed-page-shell" className="flex min-h-0 w-full flex-1 flex-col px-2 py-2 min-[400px]:px-3 min-[400px]:py-3 md:px-4
              [@media(max-height:300px)]:py-0 [:root[data-vv-short]_&]:py-0">
              <Outlet />
            </div>
          ) : fullBleed ? (
            <div data-testid="full-bleed-page-shell" className="w-full px-3 md:px-4 py-3">
              <Outlet />
            </div>
          ) : (
            <div
              data-testid="standard-page-shell"
              className="mx-auto w-full max-w-[96rem] px-4 pb-8 pt-5 md:px-6"
            >
              <Outlet />
            </div>
          )}
        </main>
      </div>

      {/* ── Expanded navigation drawer (modal overlay, all breakpoints) ──── */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-m3-medium ${
          drawerOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Scrim */}
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          className={`absolute inset-0 transition-opacity duration-m3-medium ease-m3-standard ${
            drawerOpen ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ background: 'var(--bg-overlay)' }}
          onClick={() => setDrawerOpen(false)}
        />

        {/* Drawer panel */}
        <aside
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          className={`absolute inset-y-0 start-0 flex w-[288px] flex-col bg-pane shadow-elevation-3
            transition-transform duration-m3-medium ease-m3-emphasized-decelerate ${
              drawerOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'
            }`}
          aria-label={t('nav.section_workspace')}
        >
          <div className="flex h-[76px] shrink-0 items-center justify-between px-4">
            <ProjectLockup />
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label={t('nav.close_menu')}
              className="flex size-11 items-center justify-center rounded-xl text-on-surface-variant
                transition-colors duration-m3-short hover:bg-on-surface/[0.07] hover:text-on-surface
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          {/* Rendered only when open — avoids duplicate nav text in the DOM
              (jsdom has no CSS so a hidden drawer's text would still be found). */}
          {drawerOpen && (
            <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
              <NavDrawerItems role={role} onNavigate={() => setDrawerOpen(false)} unreadTotal={unreadTotal} />
            </nav>
          )}
        </aside>
      </div>
    </div>
    </SnackbarProvider>
  );
}
